/* eslint-disable no-restricted-imports */
'use client';

import { c as _c } from "react/compiler-runtime";
import { useAppKitAccount, useAppKitNetwork, useDisconnect } from '@reown/appkit/react';
import { SCREEN_ADDRESS_ROUTE } from '@repo/address-screening/client';
import { useDebouncedValue, useIsMounted, useMandatoryContext } from '@repo/hooks';
import { createContext, type PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { type Address, isAddressEqual } from 'viem';
import { useAccount, useConfig, useConnections, useDisconnect as useWagmiDisconnect, useEnsName } from 'wagmi';
import { isE2E_Next } from '../configs/process';
import { hasExplicitInjectedConnector } from '../explicit-injected-provider';
import { useAuthorizedUserQuery } from '../hooks/useAuthorizedUserQuery';
import { getChain, isChainId } from '../utils/chain.utils';
const ADDRESS_SETTLE_MS = 250;
type WagmiConnection = ReturnType<typeof useConnections>[number];
type DisconnectWagmi = ReturnType<typeof useWagmiDisconnect>['disconnectAsync'];

// Resolves with the first rejection, if any, once every disconnect has settled.
const disconnectConnections = (connections: readonly WagmiConnection[], disconnect: DisconnectWagmi) => Promise.allSettled(connections.map(({
  connector
}) => disconnect({
  connector
}))).then(results => results.find((result): result is PromiseRejectedResult => result.status === 'rejected'));
type UseUserAccountResponse = ReturnType<typeof useUserAccountHook>;
const UserAccountContext = createContext<UseUserAccountResponse>({} as UseUserAccountResponse);
function useUserAccountHook(screenAddressEndpoint: string, disableScreening: boolean) {
  const isMounted = useIsMounted();
  const accountQuery = useAccount();
  const {
    connectors
  } = useConfig();
  const {
    isConnected,
    address
  } = useAppKitAccount();
  const {
    chainId,
    switchNetwork
  } = useAppKitNetwork();
  const {
    disconnect: disconnectAppKit
  } = useDisconnect();
  const {
    disconnectAsync: disconnectWagmi
  } = useWagmiDisconnect();
  const [screeningBlocked, setScreeningBlocked] = useState(false);
  const [screeningBlockedCount, setScreeningBlockedCount] = useState(0);
  const disconnectedAddressRef = useRef<Address | undefined>(undefined);
  // oxlint-disable-next-line typescript/no-unused-vars
  const {
    address: donotUseThisAddress,
    ...queryWithoutAddress
  } = accountQuery;
  const appKitAddress = address as Address | undefined;

  // The wagmi fallback below covers only the pre-hydration window, so latch once AppKit
  // has spoken. Monotonic on purpose: it must never unset, or the fallback re-engages.
  const [appKitHasHydrated, setAppKitHasHydrated] = useState(false);
  useEffect(() => {
    if (appKitAddress && !appKitHasHydrated) setAppKitHasHydrated(true);
  }, [appKitAddress, appKitHasHydrated]);

  /**
   * AppKit is the authority once it has hydrated. Before that it reports no address while
   * wagmi already has one from `cookieToInitialState`, so the cookie value stands in to keep
   * the shell from flashing a disconnected state.
   *
   * The fallback must stop after hydration: AppKit drops its address transiently (account
   * switch, reconnect), and resuming the fallback then swaps in wagmi's stale cookie address.
   * The two disagree, so `userAddress` oscillates between wallets on every render — rekeying
   * every address-scoped query and re-arming screening enforcement without converging.
   */
  const reportedAddress = isE2E_Next ? accountQuery.address : appKitHasHydrated ? appKitAddress : appKitAddress ?? accountQuery.address;

  // Require both AppKit and wagmi to report connected. AppKit can flip its
  // `isConnected` true before the wagmi connector has called connect() on its
  // provider; gating on both closes the desync window that would otherwise
  // surface as "Please call connect() before request()" from viem.
  // E2E mocks the wagmi connector directly, so wagmi alone is the authority there.
  const reportedIsConnected = isE2E_Next ? accountQuery.isConnected : isConnected && accountQuery.isConnected;

  // A flapping connector rekeys every address-scoped query on each flip and aborts the
  // in-flight fetch, so nothing settles. Only publish an address that has held still.
  // E2E goes through the same settle so the suites exercise the published path.
  const [userAddress] = useDebouncedValue(reportedAddress, ADDRESS_SETTLE_MS);
  const [walletIsConnected] = useDebouncedValue(reportedIsConnected, ADDRESS_SETTLE_MS);
  const {
    data: ensName
  } = useEnsName({
    address: userAddress,
    chainId: 1
  });

  // While a wallet is connected, wagmi's chain is the authority: AppKit reports the network it
  // selected, which can lag the wallet (and in E2E observes a separate config). AppKit's selection
  // only stands in while nothing is connected. `switchNetwork` stays on AppKit on purpose.
  const activeChainId = accountQuery.isConnected ? accountQuery.chainId : chainId;
  const isEvmChainId = typeof activeChainId === 'number' && isChainId(activeChainId);
  const chain = isEvmChainId ? getChain(activeChainId) : undefined;

  // The consumer's route is same-origin, so screening runs in every environment.
  // Wagmi's mock-connector E2E builds are the exception: they have no route (nor
  // Chainalysis key), so a fail-closed screen would disconnect their wallet.
  const isMockedWallet = isE2E_Next && !hasExplicitInjectedConnector(connectors);
  const screeningEnabled = !isMockedWallet && !disableScreening;
  // The hook owns the fail-closed projection; `isAuthorized` is the verdict, not raw data.
  const {
    isAuthorized
  } = useAuthorizedUserQuery(userAddress, {
    enabled: screeningEnabled,
    screenAddressEndpoint
  });

  // The effect's state lands a commit after the verdict, so mask on the verdict itself too.
  const isScreenedOut = screeningBlocked || screeningEnabled && isAuthorized === false;

  // Enforce on wagmi, not AppKit: AppKit's `disconnect()` is a no-op until its `caipAddress`
  // syncs, which lands after wagmi connects (pre-approved reconnects stayed signable). Wait for
  // `status === 'connected'` (cookie hydration reports the address while `reconnecting`) and
  // drop every connection, since wagmi promotes the next one when only the current is dropped.
  const wagmiStatus = accountQuery.status;
  const isWagmiConnected = wagmiStatus === 'connected';
  const wagmiAddress = accountQuery.address;
  const connections = useConnections();
  // Ref, not deps: AppKit's `disconnect` is a new function every render.
  // Raw, not debounced: these guards decide whether another wallet has already
  // taken over, and must see it immediately.
  const teardownRef = useRef({
    connections,
    disconnectWagmi,
    disconnectAppKit,
    reportedAddress,
    wagmiStatus
  });
  teardownRef.current = {
    connections,
    disconnectWagmi,
    disconnectAppKit,
    reportedAddress,
    wagmiStatus
  };

  // A reconnect racing the teardown (wagmi's `reconnect`, AppKit's connection sync) can re-add
  // a wagmi connection for the blocked address after AppKit already reports no address, and
  // leave it persisted with `status: 'disconnected'` — so the sweep keys on the connection
  // list once wagmi reports disconnected (the effect below owns that case). It holds while
  // wagmi is still connecting: `disconnect` publishes the connection map it captured before
  // awaiting the connector, which would drop a connector `reconnect` adds meanwhile.
  // The blocked address is remembered until it clears screening or another wallet takes over.
  const blockedAddressRef = useRef<Address | undefined>(undefined);
  const isSweepingRef = useRef(false);
  // Stable identity is load-bearing: the effect below lists it as a dep; every mutable input
  // is read through refs.
  const sweepBlockedConnections = useCallback(function sweep() {
    const blockedAddress = blockedAddressRef.current;
    const {
      connections: connections_0,
      disconnectWagmi: disconnectWagmi_0,
      reportedAddress: reportedAddress_0,
      wagmiStatus: wagmiStatus_0
    } = teardownRef.current;
    if (reportedAddress_0 || wagmiStatus_0 !== 'disconnected' || !blockedAddress || isSweepingRef.current) return;
    const lingering = connections_0.filter(({
      accounts
    }) => accounts.some(account => isAddressEqual(account, blockedAddress)));
    if (lingering.length === 0) return;
    isSweepingRef.current = true;
    disconnectConnections(lingering, disconnectWagmi_0).then(failed => {
      if (failed) console.warn('Screening sweep disconnect failed', failed.reason);
    }).finally(() => {
      isSweepingRef.current = false;
      // Connections re-added while the sweep was in flight were skipped by the guard above.
      if (teardownRef.current.connections !== connections_0) sweep();
    });
  }, []);
  useEffect(() => {
    sweepBlockedConnections();
  }, [sweepBlockedConnections, reportedAddress, userAddress, wagmiStatus, connections]);
  useEffect(() => {
    // Leaves `screeningBlocked` latched so the toast survives the disconnect.
    if (!userAddress || !isWagmiConnected) {
      disconnectedAddressRef.current = undefined;
      return;
    }
    if (!screeningEnabled || isAuthorized) {
      disconnectedAddressRef.current = undefined;
      blockedAddressRef.current = undefined;
      setScreeningBlocked(false);
      return;
    }
    // Only tear down the session we screened: AppKit's address can lag wagmi's, and the
    // settled address lags AppKit's, so a stale blocked verdict must not disconnect a wallet
    // that connected in the meantime.
    if (wagmiAddress && !isAddressEqual(wagmiAddress, userAddress)) return;
    if (reportedAddress && !isAddressEqual(reportedAddress, userAddress)) return;
    if (disconnectedAddressRef.current && isAddressEqual(disconnectedAddressRef.current, userAddress)) return;
    disconnectedAddressRef.current = userAddress;
    blockedAddressRef.current = userAddress;
    setScreeningBlocked(true);
    setScreeningBlockedCount(count => count + 1);
    console.warn('User is not authorized, disconnecting');
    const {
      connections: connections_1,
      disconnectWagmi: disconnectWagmi_1,
      disconnectAppKit: disconnectAppKit_0
    } = teardownRef.current;
    disconnectConnections(connections_1, disconnectWagmi_1).then(failed_0 => {
      if (failed_0) throw failed_0.reason;
    }).finally(() => {
      // Skip if another wallet took over meanwhile.
      const latest = teardownRef.current.reportedAddress;
      if (latest === undefined || isAddressEqual(latest, userAddress)) return disconnectAppKit_0();
    }).catch((error: unknown) => {
      // The connection survived; the next status change retries, and the account below
      // already reads as disconnected.
      console.warn('Screening disconnect failed', error);
    });
  }, [screeningEnabled, userAddress, reportedAddress, wagmiAddress, isWagmiConnected, isAuthorized]);

  // TODO: Check if this is needed with AppKit
  // useSafeAppConnectionGuard(accountQuery.connector, chainId)

  // The usage of mounted helps to overcome nextjs hydration mismatch
  // errors where the state of the user account on the server pass is different
  // than the state on the client side rehydration.
  return {
    ...queryWithoutAddress,
    isLoading: !isMounted || accountQuery.isConnecting,
    isConnecting: !isMounted || accountQuery.isConnecting,
    connector: isMounted && !isScreenedOut ? accountQuery.connector : undefined,
    // A screened-out wallet reads as disconnected here even if a connection survives.
    userAddress: isScreenedOut ? undefined : userAddress,
    isConnected: !isScreenedOut && isMounted && walletIsConnected,
    chainId: activeChainId,
    switchNetwork,
    ensName,
    chain,
    screeningBlocked,
    screeningBlockedCount
  };
}
export function UserAccountProvider(t0) {
  const $ = _c(3);
  const {
    children,
    screenAddressEndpoint: t1,
    disableScreening: t2
  } = t0;
  const screenAddressEndpoint = t1 === undefined ? SCREEN_ADDRESS_ROUTE : t1;
  const disableScreening = t2 === undefined ? false : t2;
  const hook = useUserAccountHook(screenAddressEndpoint, disableScreening);
  let t3;
  if ($[0] !== children || $[1] !== hook) {
    t3 = <UserAccountContext.Provider value={hook}>{children}</UserAccountContext.Provider>;
    $[0] = children;
    $[1] = hook;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
export const useUserAccount = () => {
  return useMandatoryContext(UserAccountContext, "UserAccount");
};